/**
 * Keyboard movement over the DB grid (owner, 30.09.2026: arrow keys "move the
 * scrollbar" — DB managers move to the next cell instead; typing on a selected
 * cell should edit it, like Excel / DBeaver). Pure: no React, no DOM.
 *
 * The grid is modelled as two axes, whatever view is on screen:
 *   horizontal view  vertical = the drawn rows (saved + pending, in draw order)
 *                    horizontal = the drawn columns (hidden ones skipped)
 *   record view      vertical = the fields (every column)
 *                    horizontal = the records shown (saved rows)
 * A position is { v, h } on those axes; a cell key is dbGridModel's cellKey.
 */
import { cellKey } from '../dbGridModel';

// kind:id:col with the id kept as text — a pending row's id is not a number,
// and dbGridModel's parseCellKey would turn it into NaN.
export function splitCellKey(key) {
  const s = String(key);
  const first = s.indexOf(':');
  const last = s.lastIndexOf(':');
  return { kind: s.slice(0, first), id: s.slice(first + 1, last), col: Number(s.slice(last + 1)) };
}

export function gridAxes({ transposed, visualRows, drawnColIndexes, recordRowIndexes }) {
  if (transposed) {
    const rows = (recordRowIndexes || []).map((id) => ({ kind: 'r', id }));
    return {
      vLen: drawnColIndexes.length,
      hLen: rows.length,
      keyAt: (v, h) => cellKey('r', rows[h].id, drawnColIndexes[v]),
      cellAt: (v, h) => ({ kind: 'r', id: rows[h].id, col: drawnColIndexes[v] }),
      posOf: (key) => {
        const { kind, id, col } = splitCellKey(key);
        const v = drawnColIndexes.indexOf(col);
        const h = rows.findIndex((r) => r.kind === kind && String(r.id) === id);
        return v === -1 || h === -1 ? null : { v, h };
      },
    };
  }
  return {
    vLen: visualRows.length,
    hLen: drawnColIndexes.length,
    keyAt: (v, h) => cellKey(visualRows[v].kind, visualRows[v].id, drawnColIndexes[h]),
    // Raw ids: a pending row's id is not a number, so never parse it back.
    cellAt: (v, h) => ({ kind: visualRows[v].kind, id: visualRows[v].id, col: drawnColIndexes[h] }),
    posOf: (key) => {
      const { kind, id, col } = splitCellKey(key);
      const v = visualRows.findIndex((r) => r.kind === kind && String(r.id) === id);
      const h = drawnColIndexes.indexOf(col);
      return v === -1 || h === -1 ? null : { v, h };
    },
  };
}

const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));

/**
 * Where a navigation key takes the cursor, or null if the key is not one.
 * Arrow = one step; Ctrl/Cmd+Arrow = the edge; Home/End = first/last column
 * (with Ctrl: first/last cell); PageUp/PageDown = one page of rows.
 */
export function moveCursor(pos, { key, ctrl = false }, { vLen, hLen, pageSize = 20 }) {
  if (!pos || vLen === 0 || hLen === 0) return null;
  const lastV = vLen - 1;
  const lastH = hLen - 1;
  switch (key) {
    case 'ArrowUp':
      return { v: ctrl ? 0 : clamp(pos.v - 1, 0, lastV), h: pos.h };
    case 'ArrowDown':
      return { v: ctrl ? lastV : clamp(pos.v + 1, 0, lastV), h: pos.h };
    case 'ArrowLeft':
      return { v: pos.v, h: ctrl ? 0 : clamp(pos.h - 1, 0, lastH) };
    case 'ArrowRight':
      return { v: pos.v, h: ctrl ? lastH : clamp(pos.h + 1, 0, lastH) };
    case 'Home':
      return ctrl ? { v: 0, h: 0 } : { v: pos.v, h: 0 };
    case 'End':
      return ctrl ? { v: lastV, h: lastH } : { v: pos.v, h: lastH };
    case 'PageUp':
      return { v: clamp(pos.v - Math.max(1, pageSize), 0, lastV), h: pos.h };
    case 'PageDown':
      return { v: clamp(pos.v + Math.max(1, pageSize), 0, lastV), h: pos.h };
    default:
      return null;
  }
}

/** Every cell key in the rectangle between two positions (inclusive). */
export function rectKeys(axes, a, b) {
  const out = new Set();
  const [v0, v1] = a.v <= b.v ? [a.v, b.v] : [b.v, a.v];
  const [h0, h1] = a.h <= b.h ? [a.h, b.h] : [b.h, a.h];
  for (let v = v0; v <= v1; v += 1) {
    for (let h = h0; h <= h1; h += 1) out.add(axes.keyAt(v, h));
  }
  return out;
}

/**
 * Does this keydown type a character (so it may start editing a cell)?
 * One visible character, no Ctrl/Cmd — EXCEPT AltGr, which Windows reports as
 * Ctrl+Alt (German @ € { come that way). Dead keys, IME composition and
 * named keys (Enter, F2, arrows…) are not typing.
 */
export function isPrintableKey(e) {
  if (!e || e.isComposing || e.keyCode === 229) return false;
  const key = e.key || '';
  if (key.length !== 1) return false; // 'Dead', 'Process', 'Enter', 'F2' …
  const altGr = (typeof e.getModifierState === 'function' && e.getModifierState('AltGraph')) || (e.ctrlKey && e.altKey);
  if (e.metaKey) return false;
  if (e.ctrlKey && !altGr) return false;
  return true;
}
